import { Menu as BaseMenu } from '@base-ui/react/menu'

import { Checkbox } from '@gto/ui/checkbox'
import { cn } from '@gto/ui/cn'

import { menuItemVariants } from './item.styles'

type CheckboxItemProps = Omit<BaseMenu.CheckboxItem.Props, 'render'>

/**
 * Menu row that toggles a boolean. Base UI owns the `data-checked` state and
 * the `onCheckedChange` lifecycle; the leading visual is the shared
 * `Checkbox` (no separate indicator needed). The Checkbox is purely decorative
 * — pointer events are disabled so clicks land on the row.
 *
 * `children` are siblings of the box, NOT the Checkbox's own `label` prop.
 * That prop wraps both in a second `inline-flex … gap-2 h-10` layer, which
 * duplicates the row's own flex+gap, forces a 40px box inside a 28px row, and
 * leaves the text content-sized — so a row wanting trailing content (a hotkey
 * hint, a badge) could only stretch it by reaching through two levels of
 * Checkbox-internal markup. Rendered flat, the row's flex owns the layout and
 * a caller just puts `flex-1` on its own child.
 */
export const CheckboxItem = ({ children, className, ...props }: CheckboxItemProps) => {
  return (
    <BaseMenu.CheckboxItem
      // `checkedTint: 'none'`: the box already shows the state — a green label (or a green
      // leading icon) beside a green box reads as two indicators. RadioItem keeps the
      // default tint; its label colour IS its only checked indicator.
      className={cn(menuItemVariants({ checkedTint: 'none' }), className)}
      {...props}
      render={(renderProps, state) => (
        <div {...renderProps}>
          <Checkbox
            checked={state.checked}
            className="pointer-events-none [&_svg]:!text-primary-contrastText"
            size="small"
            tabIndex={-1}
          />
          {children}
        </div>
      )}
    />
  )
}
